import { auth as firebaseAuth } from "../config/firebase";
import {
  signInWithEmailAndPassword as firebaseSignIn,
  createUserWithEmailAndPassword as firebaseCreateUser,
  sendPasswordResetEmail as firebaseSendReset,
  sendEmailVerification as firebaseSendVerification,
  updateProfile as firebaseUpdateProfile,
  signOut as firebaseSignOut,
} from "firebase/auth";

import {
  auth as cognitoAuth,
  getCurrentUser as getCognitoUser,
} from "../config/cognito";
import {
  signIn as cognitoSignIn,
  signUp as cognitoSignUp,
  confirmSignUp as cognitoConfirmSignUp,
  resetPassword as cognitoResetPassword,
  confirmResetPassword as cognitoConfirmReset,
  signOut as cognitoSignOut,
  fetchAuthSession,
} from "@aws-amplify/auth";

// Determine which auth provider to use based on configuration
export const useCognito = !!process.env.REACT_APP_COGNITO_CLIENT_ID;

// Get the appropriate auth object
export const getAuth = () => (useCognito ? cognitoAuth : firebaseAuth);

// Simple signOut function that works with both providers
export const signOut = async () => {
  if (useCognito) {
    return cognitoSignOut();
  } else {
    return firebaseSignOut(firebaseAuth);
  }
};

// Function to get current user
export const getCurrentUser = async () => {
  if (useCognito) {
    return getCognitoUser();
  } else {
    return firebaseAuth.currentUser;
  }
};

// Sign in with email and password
export const signInWithEmail = async (params) => {
  if (useCognito) {
    return cognitoSignIn(params);
  } else {
    const email = params.email || params.username;
    const password = params.password;
    return firebaseSignIn(firebaseAuth, email, password);
  }
};

// Sign up with email and password
export const signUpWithEmail = async (params) => {
  if (useCognito) {
    return cognitoSignUp(params);
  } else {
    const email = params.email || params.username;
    const password = params.password;
    const userCredential = await firebaseCreateUser(
      firebaseAuth,
      email,
      password,
    );
    return userCredential;
  }
};

// Confirm sign up (verification code)
export const confirmUserSignUp = async (email, code) => {
  if (useCognito) {
    return cognitoConfirmSignUp({ username: email, confirmationCode: code });
  }
  return null;
};

// Send email verification
export const sendVerificationEmail = async (user) => {
  if (!useCognito && user) {
    return firebaseSendVerification(user);
  }
  return null;
};

// Reset password
export const resetUserPassword = async (email) => {
  if (useCognito) {
    return cognitoResetPassword({ username: email });
  } else {
    return firebaseSendReset(firebaseAuth, email);
  }
};

// Confirm reset password with code
export const confirmUserPasswordReset = async (email, code, newPassword) => {
  if (useCognito) {
    return cognitoConfirmReset({
      username: email,
      confirmationCode: code,
      newPassword,
    });
  }
  return null;
};

// Get authentication headers for API requests
export const getAuthHeaders = async () => {
  try {
    if (useCognito) {
      try {
        const session = await fetchAuthSession();

        if (session?.tokens?.accessToken) {
          const tokenString =
            typeof session.tokens.accessToken === "string"
              ? session.tokens.accessToken
              : session.tokens.accessToken.toString();

          const email =
            session.tokens.idToken?.payload?.email ||
            session.tokens.accessToken?.payload?.username ||
            "unknown-user";

          return {
            Authorization: `Bearer ${tokenString}`,
            "x-user": email,
          };
        } else {
          console.error("No valid Cognito session tokens found");
          throw new Error("No valid authentication tokens available");
        }
      } catch (error) {
        console.error("Error getting Cognito session:", error);
        throw error;
      }
    } else if (firebaseAuth.currentUser) {
      const token = await firebaseAuth.currentUser.getIdToken();
      return {
        Authorization: `Bearer ${token}`,
        "x-user": firebaseAuth.currentUser.email,
      };
    }
    return {};
  } catch (error) {
    console.error("Error getting auth headers:", error);
    throw error;
  }
};

// Get the current authenticated user email
export const getCurrentUserEmail = async () => {
  try {
    if (useCognito) {
      try {
        const session = await fetchAuthSession();
        return session?.tokens?.idToken?.payload?.email || null;
      } catch {
        return null;
      }
    } else {
      return firebaseAuth.currentUser?.email || null;
    }
  } catch (error) {
    console.error("Error getting user email:", error);
    return null;
  }
};

export const updateProfile = async (user, profileData) => {
  if (useCognito) {
    // Cognito doesn't directly support updateProfile
    return null;
  } else {
    return firebaseUpdateProfile(user, profileData);
  }
};
